import {
  fetchForecastMatchesByUser,
  fetchForecastGroupsByUser,
  patchForecastMatches,
  patchForecastGroups
} from '../service/forecast';
import { activateComodin } from '../service/torneo';
import { getUser, udpateUserStats } from '../service/user';
import { loading } from '@actions';

import { isPlayerDirty } from '@selectors/players';
import { selectBracketForecastMatches } from '@selectors/matches';
import { isWordCupStarted } from '@selectors/clock';
import {
  filterForecastRowsForWildcard,
  isForecastMidInWildcardScope,
} from '@utils/fase';
import {
  hasBothTeams,
  hasCompleteForecastScores,
  hasPartialForecastScores,
} from '@utils/match';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';

import { message } from 'antd';

export const loadForecast = userId => (dispatch, getState) => {
  let { jwToken, forecastMatches, forecastGroups, forecastOwner, user } = getState();

  const targetUserId = userId || get(user, '_id');
  const cacheIsValid =
    !isEmpty(forecastMatches) &&
    !isEmpty(forecastGroups) &&
    String(forecastOwner || '') === String(targetUserId || '');

  if (cacheIsValid) {
    return Promise.resolve([
      getState().forecastMatches,
      getState().forecastGroups
    ]);
  }

  dispatch(loading(true));

  return Promise.all([
    fetchForecastMatchesByUser(jwToken, targetUserId),
    fetchForecastGroupsByUser(jwToken, targetUserId)
  ]).then(([fMatches, fGroups]) => {
    dispatch({ type: 'FORECAST_MATCHES_SET', payload: fMatches.data });
    dispatch({ type: 'FORECAST_GROUPS_SET', payload: fGroups.data });
    dispatch({ type: 'FORECAST_OWNER_SET', payload: String(targetUserId) });
    dispatch(loading(false));
    return [fMatches.data, fGroups.data];
  });
};

const pickClearedField = (match, field) =>
  Object.prototype.hasOwnProperty.call(match, field) ? match[field] : undefined;

const buildMatchPatch = (match, bracketByMid, preferBracketTeams = false) => {
  const bracketMatch = get(bracketByMid, match.mid, {});

  return omitBy(
    {
      mid: match.mid,
      teamHome: preferBracketTeams
        ? bracketMatch.teamHome ?? match.teamHome
        : match.teamHome ?? bracketMatch.teamHome,
      teamAway: preferBracketTeams
        ? bracketMatch.teamAway ?? match.teamAway
        : match.teamAway ?? bracketMatch.teamAway,
      scoreHome: pickClearedField(match, 'scoreHome'),
      scoreAway: pickClearedField(match, 'scoreAway'),
      winner: pickClearedField(match, 'winner'),
    },
    isUndefined
  );
};

const hasVisibleForecastPick = (match) =>
  Boolean(
    match &&
    (match.scoreHome != null ||
      match.scoreAway != null ||
      (match.winner != null && match.winner !== '') ||
      (match.teamHome && match.teamAway))
  );

/** En comodín, persiste lo que ve el usuario en el bracket (selector), no solo filas dirty. */
const buildWildcardScopePatches = (bracketByMid, wildcardScope) =>
  filterForecastRowsForWildcard(
    Object.values(bracketByMid || {})
      .filter(hasVisibleForecastPick)
      .map((match) =>
        omitBy(
          {
            mid: match.mid,
            teamHome: match.teamHome,
            teamAway: match.teamAway,
            scoreHome: pickClearedField(match, 'scoreHome'),
            scoreAway: pickClearedField(match, 'scoreAway'),
            winner: pickClearedField(match, 'winner'),
          },
          isUndefined
        )
      ),
    wildcardScope
  );

const isSyncStub = (match) =>
  match.teamHome === undefined &&
  match.teamAway === undefined &&
  match.scoreHome === undefined &&
  match.scoreAway === undefined &&
  match.winner === undefined;

const buildDirtyMatchPatches = (
  dirtyMatches,
  bracketByMid,
  preferBracketTeams
) =>
  dirtyMatches
    .filter((match) => !isSyncStub(match))
    .map((match) => buildMatchPatch(match, bracketByMid, preferBracketTeams));

const mergeForecastLists = (primary = [], secondary = []) => {
  const byMid = keyBy(primary, 'mid');
  (secondary || []).forEach((row) => {
    if (row?.mid != null) {
      byMid[row.mid] = { ...byMid[row.mid], ...row };
    }
  });
  return Object.values(byMid);
};

export const saveForecast = () => async (dispatch, getState) => {
  dispatch({ type: 'RESET_TIME' });

  const state = getState();
  let { jwToken, forecastMatches, forecastGroups, user, players, wildcardMode = {} } = state;
  const knockoutEditPermitEnabled =
    get(state, 'user.knockoutEditPermit.enabled', false) === true &&
    (Number(get(state, 'user.knockoutEditPermit.remainingUses', 0)) || 0) > 0;

  if (isWordCupStarted(state) && !wildcardMode.enabled && !knockoutEditPermitEnabled) {
    message.error('No se puede guardar pronostico, el Mundial ya ha iniciado');
    return;
  }
  const hasRoundOf32 = (state.matches || []).some((m) => m && m.round === 'roundof32');
  const wildcardScope =
    wildcardMode.enabled && wildcardMode.faseActivacion
      ? {
        faseActivacion: wildcardMode.faseActivacion,
        matches: state.matches,
        hasRoundOf32,
      }
      : null;
  const knockoutPermitScope =
    !wildcardScope && knockoutEditPermitEnabled
      ? {
        faseActivacion: '16avos',
        matches: state.matches,
        hasRoundOf32,
      }
      : null;

  let dirtyMatches = filter(forecastMatches, { dirty: true });
  if (wildcardScope) {
    dirtyMatches = filterForecastRowsForWildcard(dirtyMatches, wildcardScope);
  } else if (knockoutPermitScope) {
    dirtyMatches = filterForecastRowsForWildcard(dirtyMatches, knockoutPermitScope);
  }

  let dirtyGroups = filter(forecastGroups, { dirty: true });
  if (wildcardMode.enabled) {
    dirtyGroups = [];
  }

  const bracketByMid = keyBy(
    Object.values(selectBracketForecastMatches(state) || {}),
    'mid'
  );
  const preferBracketTeams = Boolean(wildcardMode.enabled);
  let matchPatches;

  if (wildcardScope) {
    matchPatches = buildWildcardScopePatches(bracketByMid, wildcardScope);
  } else if (knockoutPermitScope) {
    matchPatches = buildWildcardScopePatches(bracketByMid, knockoutPermitScope);
  } else {
    matchPatches = buildDirtyMatchPatches(
      dirtyMatches,
      bracketByMid,
      preferBracketTeams
    );
  }

  if (wildcardScope) {
    const scopeMatches = Object.values(bracketByMid || {}).filter((match) =>
      isForecastMidInWildcardScope(
        match.mid,
        wildcardScope.faseActivacion,
        wildcardScope.matches,
        wildcardScope.hasRoundOf32
      )
    );

    const scorableMatches = scopeMatches.filter(hasBothTeams);

    if (
      scorableMatches.length === 0 ||
      scorableMatches.some((match) => !hasCompleteForecastScores(match))
    ) {
      message.error(
        'Completá todos los resultados del comodín antes de guardar.'
      );
      return;
    }
  } else {
    const matchesToValidate = dirtyMatches.filter((match) => !isSyncStub(match));

    if (matchesToValidate.some(hasPartialForecastScores)) {
      message.error('Completá ambos resultados de cada partido.');
      return;
    }
  }

  if (wildcardScope && matchPatches.length === 0) {
    message.error('No hay pronósticos para guardar en el alcance del comodín.');
    return;
  }
  if (knockoutPermitScope && matchPatches.length === 0) {
    message.error('No hay pronósticos para guardar en el alcance del permiso especial.');
    return;
  }

  dispatch({ type: 'FORECAST_SAVING', payload: true });

  try {
    // Si está en modo wildcard, primero registrar el uso del comodín
    if (wildcardMode.enabled && wildcardMode.faseActivacion) {
      const comodinResponse = await activateComodin(jwToken, wildcardMode.faseActivacion);

      if (comodinResponse.data) {
        dispatch({
          type: 'AUTH_UPDATE_USER',
          payload: {
            comodinesDisponibles: comodinResponse.data.comodinesDisponibles,
            comodinesUsados: comodinResponse.data.comodinesUsados,
            mundialIdeal: false
          },
        });
      }
    }

    const results = await Promise.all([
      isEmpty(matchPatches)
        ? Promise.resolve(forecastMatches)
        : patchForecastMatches(
          jwToken,
          matchPatches,
          Boolean(wildcardMode.enabled),
          wildcardMode.faseActivacion
        ),
      isEmpty(dirtyGroups)
        ? Promise.resolve(forecastGroups)
        : patchForecastGroups(jwToken, dirtyGroups),
      isPlayerDirty(getState())
        ? udpateUserStats(jwToken, user._id, { scorer: players.selected })
        : Promise.resolve(get(getState(), 'user.scorer'))
    ]);

    dispatch({ type: 'FORECAST_MATCHES_RESET_DIRTY' });
    dispatch({ type: 'FORECAST_GROUPS_RESET_DIRTY' });
    dispatch({
      type: 'AUTH_UPDATE_USER',
      payload: { scorer: players.selected }
    });
    dispatch({ type: 'WILDCARD_MODE_DISABLE' });

    if ((wildcardMode.enabled || knockoutEditPermitEnabled) && user?._id) {
      const patchedRows = Array.isArray(results[0]?.data)
        ? results[0].data
        : results[0]?.data
          ? [results[0].data]
          : [];
      const { data: refetched } = await fetchForecastMatchesByUser(jwToken, user._id);
      const refreshedUser = await getUser(jwToken, user._id);
      dispatch({ type: 'AUTH_SET_USER', payload: refreshedUser.data });
      dispatch({
        type: 'FORECAST_MATCHES_SET',
        payload: mergeForecastLists(refetched, patchedRows),
      });
    }

    dispatch({ type: 'FORECAST_SAVING', payload: false });

    message.success('Pronóstico actualizado con éxito!');
    return [results[0].data, results[1].data];
  } catch (error) {
    dispatch({ type: 'FORECAST_SAVING', payload: false });
    console.error('Error al guardar pronóstico:', error);
    message.error(get(error, 'response.data.message', 'Error al guardar el pronóstico'));
    throw error;
  }
};

export const loadForecastUser = userId => (dispatch, getState) => {
  let { jwToken, forecastUser } = getState();

  if (get(forecastUser, '_id') === userId) {
    return Promise.resolve(getState().forecastUser);
  }

  return getUser(jwToken, userId).then(({ data }) => {
    dispatch({ type: 'FORECAST_USER_SET', payload: data });
    return data;
  });
};
